import React, { useState } from 'react';
import { Paper, Box, Typography } from '@material-ui/core';
import * as yup from 'yup';
import { DEFAULT_PAPER_ELEVATION } from 'common/constants';
import { ShopCartItem } from 'store/ducks/shopCart/types';
import { SIZES } from 'views/pages/Product/common/constants';
import ProductsItemLabel from 'views/pages/Products/components/Products/components/Item/Label';
import TextField from 'common/components/TextField';
import { formatPrice } from 'common/functions';
import { makeProductClasses, makeProductPaperClasses } from 'styles/pages/checkout';

type Props = {
    changeProductQuantity: Function;
    isPromoter: boolean;
    item: ShopCartItem;
    loading: boolean;
    removeItemFromShopCart: Function;
};

const Error = ({ error }: { error: string }) => {
    const { errorPanel } = makeProductClasses();

    return error ? <Box className={errorPanel}>{error}</Box> : null;
};

const validation = (value: number | string | null, setError: Function, validQuantity: number) =>
    yup
        .number()
        .transform((currentValue: any, originalValue: any) => {
            return originalValue === '' || originalValue === null ? undefined : currentValue;
        })
        .required('Podaj liczbę egzemplarzy.')
        .min(validQuantity, `Minimalna liczba egzemplarzy: ${validQuantity}.`)
        .validate(value)
        .then(() => setError(''))
        .catch(({ errors }) => setError(errors));

const Product: React.FC<Props> = ({
    changeProductQuantity,
    isPromoter,
    item,
    loading,
    removeItemFromShopCart,
}) => {
    const {
        code,
        details: { id, images, minQuantity, name, price, products },
        quantity: quantityRedux,
    } = item;

    const type = item.details.type;

    const [error, setError] = useState('');
    const [quantity, setQuantity] = useState<number | string>(quantityRedux);

    const [{ absolutePath }] = images?.thumbnails?.items[0].sizes.filter(
        ({ size }) => size === SIZES.SMALL,
    );

    const productPaperClasses = makeProductPaperClasses();
    const { cover, infoPanel, pricePanel, productsList, remove, wrapper } = makeProductClasses();

    const validQuantity = isPromoter ? 1 : minQuantity;

    const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = e.currentTarget;
        const value = target.value;

        // https://bugzilla.mozilla.org/show_bug.cgi?id=1012818
        target.focus();

        setQuantity(value);
        validation(value, setError, validQuantity);
    };

    const handleBlur = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (error) return;

        changeProductQuantity(code, Number(quantity), id);
    };

    return (
        <Paper classes={productPaperClasses} elevation={DEFAULT_PAPER_ELEVATION} square>
            <Box className={wrapper} py={2} px={4}>
                <Box className={infoPanel}>
                    <img alt="" className={cover} src={absolutePath} />
                    <Box>
                        <Typography component="p">{name}</Typography>
                        <ProductsItemLabel type={type} />
                        {products?.length && (
                            <ul className={productsList}>
                                {products?.map(({ name, quantity }) => (
                                    <li>
                                        {quantity}x {name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Box>
                </Box>
                <Box className={pricePanel}>
                    <span>{price && formatPrice(price)}</span>
                    {item.details.type !== 'GRANT_SET' && (
                        <>
                            <TextField
                                disabled={loading}
                                inputProps={{
                                    min: validQuantity,
                                    style: {
                                        maxWidth: '3.8rem',
                                    },
                                    onChange: handleChange,
                                    onBlur: handleBlur,
                                }}
                                label="egz."
                                name="price"
                                type="number"
                                value={quantity ?? ''}
                                variant="outlined"
                            />
                            <Error error={error} />
                        </>
                    )}
                </Box>
                <div className={remove} onClick={() => removeItemFromShopCart(id, quantity)}>
                    usuń
                </div>
            </Box>
        </Paper>
    );
};

export default Product;
